// src/pages/PreferencesPage.js
import React, { useEffect, useMemo, useState } from "react";
import Card from "../ui/Card";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { clearLocalOrgCache } from "../utils/orgData";
import { DEFAULT_ORG_ID } from "../utils/brandAssets";

const BG_KEY = "pref_bg";
const COMPACT_KEY = "pref_compact_rows";
const TS_KEY = "pref_timestamps";
const bgKeyForOrg = (orgId) => `${BG_KEY}__${orgId || DEFAULT_ORG_ID}`;
const normalizeBgClass = (value) => (value === "bg-none" || value === "bg-photo" ? value : "bg-hero-grid");
const readBgForOrg = (orgId) => {
  try {
    return normalizeBgClass(
      localStorage.getItem(bgKeyForOrg(orgId)) ||
      localStorage.getItem(BG_KEY) ||
      "bg-hero-grid"
    );
  } catch {
    return "bg-hero-grid";
  }
};

export default function PreferencesPage() {
  const { user, activeOrgId } = useAuth();
  const resolvedOrgId = activeOrgId || user?.orgId || DEFAULT_ORG_ID;
  const { isDark, setDark } = useTheme();
  const [bgMode, setBgMode] = useState(() => readBgForOrg(DEFAULT_ORG_ID));
  const [compactRows, setCompactRows] = useState(() => localStorage.getItem(COMPACT_KEY) === "true");
  const [showTs, setShowTs] = useState(() => localStorage.getItem(TS_KEY) === "true");
  const [cacheNotice, setCacheNotice] = useState("");

  // Available backgrounds per theme (added photo options)
  const allowedBg = useMemo(() => {
    return ["bg-none", "bg-hero-grid", "bg-photo"];
  }, []);

  // If theme flips and the current choice isn't valid, auto-correct
  useEffect(() => {
    if (!allowedBg.includes(bgMode)) {
      setBgMode("bg-hero-grid");
    }
  }, [isDark, allowedBg, bgMode]);

  useEffect(() => {
    setBgMode(readBgForOrg(resolvedOrgId));
  }, [resolvedOrgId]);

  // Apply/persist background
  useEffect(() => {
    try {
      localStorage.setItem(bgKeyForOrg(resolvedOrgId), bgMode);
      localStorage.setItem(BG_KEY, bgMode); // legacy/global fallback
      window.dispatchEvent(new Event("pref-changed"));
    } catch {}
  }, [bgMode, resolvedOrgId]);

  // Tables compact
  useEffect(() => {
    document.body.classList.toggle("compact-tables", compactRows);
    localStorage.setItem(COMPACT_KEY, String(compactRows));
  }, [compactRows]);

  // Chat timestamps
  useEffect(() => {
    localStorage.setItem(TS_KEY, String(showTs));
  }, [showTs]);

  return (
    <div className="nia-page">
      <Card className="nia-hero">
        <div className="nia-header">
          <div>
            <h2>Preferences</h2>
            <p>Personalize the Nia COACH experience without compromising performance or focus.</p>
          </div>
        </div>
      </Card>

      <div className="nia-grid nia-grid--2">
        <Card title="Appearance">
          <div className="nia-form">
            <label>Theme</label>
            <div className="toggle-container" style={{ justifyContent: "flex-start", gap: 12 }}>
              <span role="img" aria-label="light">🌞</span>
              <label className="switch">
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={isDark}
                  checked={isDark}
                  onChange={() => setDark(!isDark)}
                />
                <span className="slider"></span>
              </label>
              <span role="img" aria-label="dark">🌙</span>
            </div>

            <label htmlFor="bg-select">App background</label>
            <select id="bg-select" value={bgMode} onChange={e => setBgMode(e.target.value)}>
              {allowedBg.includes("bg-none")        && <option value="bg-none">None</option>}
              {allowedBg.includes("bg-hero-grid")   && <option value="bg-hero-grid">Grid Overlay (Subtle)</option>}
              {allowedBg.includes("bg-photo")       && <option value="bg-photo">Photo Overlay (Subtle)</option>}
            </select>
            <p className="nia-muted" style={{ marginTop: 6 }}>
              Brand textures or photos placed behind the app content.
            </p>
          </div>
        </Card>

        <Card title="Experience">
          <div className="nia-form">
            <label>Tables</label>
            <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="checkbox"
                checked={compactRows}
                onChange={(e) => setCompactRows(e.target.checked)}
              />
              Compact rows
            </label>

            <label style={{ marginTop: 8 }}>Chat</label>
            <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="checkbox"
                checked={showTs}
                onChange={(e) => setShowTs(e.target.checked)}
              />
              Show message timestamps
            </label>

            <label style={{ marginTop: 14 }}>Storage</label>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                const orgId = activeOrgId || user?.orgId || "org_default";
                clearLocalOrgCache(orgId, user?.uid || "");
                setCacheNotice(`Local cache cleared for ${orgId}.`);
              }}
            >
              Reset local cache for active org
            </button>
            {cacheNotice ? <p className="nia-muted" style={{ marginTop: 6 }}>{cacheNotice}</p> : null}
          </div>
        </Card>
      </div>
    </div>
  );
}
